import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { userActions } from '../../actions';
import { Button, Form, Segment, Modal, Header, Message, Icon } from 'semantic-ui-react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import * as Yup from 'yup';
import DatePicker, { utils } from 'react-modern-calendar-datepicker';
import 'react-modern-calendar-datepicker/lib/DatePicker.css';

const regexMobile = /^09[0-9]{9}$/i;

const AccountForm = () => {
  const { t } = useTranslation();
  const [openModal, setOpenModal] = useState(false);
  const [hideMessage, setHideMessage] = useState(true);
  const dispatch = useDispatch();
  const authState = useSelector(state => state.authentication.authState);
  const updateState = useSelector(state => state.user.updateState);
  const error = useSelector(state => state.user.error);
  const user = useSelector(state => state.authentication.user);
  const [selectedDay, setSelectedDay] = useState(null);
  const initialValues = _.defaultsDeep(
    _.pick(user, [
      'userId',
      'username',
      'password',
      'details.firstname',
      'details.lastname',
      'details.nationalId',
      'details.mobile',
      'details.email',
      'details.gender',
      'details.birthdate',
      'details.address',
      'details.postalCode',
      'details.country',
      'details.city',
    ]),
    {
      username: '',
      password: '',
      details: {
        firstname: '',
        lastname: '',
        nationalId: '',
        mobile: '',
        email: '',
        gender: '',
        birthdate: '',
        address: '',
        postalCode: '',
        country: '',
        city: '',
      },
    },
  );
  const AccountSchema = Yup.object().shape({
    password: Yup.lazy(value =>
      !value
        ? Yup.string()
        : Yup.string()
            .min(6, t('yupPasswordValidate'))
            .required(t('yupPasswordReq')),
    ),
    confirmPassword: Yup.lazy(value =>
      !value
        ? Yup.string()
        : Yup.string()
            .required(t('yupConfirmPassReq'))
            .oneOf([Yup.ref('password'), null], t('yupConfirmPassValidate')),
    ),
    details: Yup.object().shape({
      firstname: Yup.string().nullable(),
      lastname: Yup.string().nullable(),
      nationalId: Yup.string().nullable(),
      mobile: Yup.string()
        .matches(new RegExp(regexMobile.source), t('yupMobileRegex'))
        .nullable(),
      email: Yup.string()
        .email(t('yupEmailValidate'))
        .nullable(),
      gender: Yup.string().oneOf(['male', 'female']),
      birthdate: Yup.string(),
      address: Yup.string().nullable(),
      postalCode: Yup.string().nullable(),
      country: Yup.string().nullable(),
      city: Yup.string().nullable(),
    }),
  });
  const AuthenticateSchema = Yup.object().shape({
    password: Yup.string().required(t('yupPasswordReq')),
  });

  const formik = useFormik({
    initialValues,
    validationSchema: AccountSchema,
    onSubmit: values => {
      const _values = { ...values };
      if (authState !== 'authenticated') {
        delete _values.password;
        delete _values.mobile;
        delete _values.email;
      }
      if (!_values.password) delete _values.password;
      setHideMessage(true);
      dispatch(userActions.update(_values));
    },
  });

  const formikAuth = useFormik({
    initialValues: {
      password: '',
    },
    validationSchema: AuthenticateSchema,
    onSubmit: values => {
      dispatch(userActions.authenticate(values));
    },
    onReset: () => {
      setOpenModal(false);
    },
  });

  useEffect(() => {
    setHideMessage(true);
  }, []);

  useEffect(() => {
    const date = _.get(user, 'details.birthdate', '//').split('/');
    if (date[0])
      setSelectedDay({ year: parseInt(date[0]), month: parseInt(date[1]), day: parseInt(date[2]) });
  }, [user]);

  useEffect(() => {
    if (authState === 'failed') {
      formikAuth.setFieldError('password', t('yupPasswordFailed'));
    } else if (authState === 'authenticated') {
      setOpenModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authState]);

  useEffect(() => {
    if (updateState === 'failed' || updateState === 'updated') {
      setHideMessage(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateState]);

  useEffect(() => {
    if (_.has(selectedDay, 'year')) {
      formik.values.details.birthdate = `${selectedDay.year}/${selectedDay.month}/${selectedDay.day}`;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDay]);

  // eslint-disable-next-line react/prop-types
  const renderCustomInput = ({ ref }) => (
    <Form.Input
      disabled={updateState === 'updating'}
      readOnly={true}
      iconPosition="left"
      fluid
      type="text"
      name="details.birthdate"
      value={
        _.has(selectedDay, 'year')
          ? `${selectedDay.year}/${selectedDay.month}/${selectedDay.day}`
          : ''
      }
    >
      <input ref={ref} />
      <Icon name="calendar alternate outline" />
    </Form.Input>
  );

  return (
    <>
      <Form size="large" className="settingForm">
        <Message
          className="enteryFormMessage"
          hidden={hideMessage}
          floating
          negative={updateState === 'failed'}
          positive={updateState === 'updated'}
          content={
            updateState === 'updated'
              ? t('accSettingsErrorHeaderSuccess')
              : t('accSettingsErrorHeaderFail')
          }
        />
        <Segment basic>
          <span className="label">{t('firstname')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.firstname')
                ? _.get(formik.errors, 'details.firstname')
                : null
            }
            disabled={updateState === 'updating'}
            fluid
            type="text"
            name="details.firstname"
            value={formik.values.details.firstname}
            onChange={formik.handleChange}
          />
          <span className="label">{t('lastname')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.lastname')
                ? _.get(formik.errors, 'details.lastname')
                : null
            }
            disabled={updateState === 'updating'}
            fluid
            type="text"
            name="details.lastname"
            value={formik.values.details.lastname}
            onChange={formik.handleChange}
          />
          <span className="label">{t('gender')}</span>
          <Form.Select
            error={
              _.has(formik.touched, 'details.gender')
                ? _.get(formik.errors, 'details.gender')
                : null
            }
            disabled={updateState === 'updating'}
            fluid
            basic
            type="text"
            name="details.gender"
            options={[
              { key: 'm', text: t('male'), value: 'male' },
              { key: 'f', text: t('female'), value: 'female' },
            ]}
            value={formik.values.details.gender}
            onChange={(e, { name, value }) => formik.setFieldValue(name, value)}
          />
          <span className="label">{t('birthdate')}</span>
          <DatePicker
            locale="fa"
            value={selectedDay}
            onChange={setSelectedDay}
            maximumDate={utils('fa').getToday()}
            renderInput={renderCustomInput}
            shouldHighlightWeekends
          />
          <span className="label">{t('mobile')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.mobile')
                ? _.get(formik.errors, 'details.mobile')
                : null
            }
            disabled={updateState === 'updating'}
            readOnly={authState !== 'authenticated'}
            icon={authState === 'authenticated' ? 'unlock' : 'lock'}
            iconPosition="left"
            fluid
            type="text"
            name="details.mobile"
            value={formik.values.details.mobile}
            onChange={formik.handleChange}
            onClick={() => authState !== 'authenticated' && setOpenModal(true)}
          />
          <span className="label">{t('email')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.email') ? _.get(formik.errors, 'details.email') : null
            }
            disabled={updateState === 'updating'}
            readOnly={authState !== 'authenticated'}
            icon={authState === 'authenticated' ? 'unlock' : 'lock'}
            iconPosition="left"
            placeholder={t('email')}
            fluid
            type="text"
            name="details.email"
            value={formik.values.details.email}
            onChange={formik.handleChange}
            onClick={() => authState !== 'authenticated' && setOpenModal(true)}
          />
          <span className="label">{t('address')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.address')
                ? _.get(formik.errors, 'details.address')
                : null
            }
            disabled={updateState === 'updating'}
            fluid
            type="text"
            name="details.address"
            value={formik.values.details.address}
            onChange={formik.handleChange}
          />
          <span className="label">{t('postalCode')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.postalCode')
                ? _.get(formik.errors, 'details.postalCode')
                : null
            }
            disabled={updateState === 'updating'}
            fluid
            type="text"
            name="details.postalCode"
            value={formik.values.details.postalCode}
            onChange={formik.handleChange}
          />
          <span className="label">{t('country')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.country')
                ? _.get(formik.errors, 'details.country')
                : null
            }
            disabled={updateState === 'updating'}
            fluid
            type="text"
            name="details.country"
            value={formik.values.details.country}
            onChange={formik.handleChange}
          />
          <span className="label">{t('city')}</span>
          <Form.Input
            error={
              _.has(formik.touched, 'details.city') ? _.get(formik.errors, 'details.city') : null
            }
            disabled={updateState === 'updating'}
            fluid
            type="text"
            name="details.city"
            value={formik.values.details.city}
            onChange={formik.handleChange}
          />
          <span className="label">{t('newPassword')}</span>
          <Form.Input
            error={formik.touched.password ? _.get(formik.errors, 'password') : null}
            disabled={updateState === 'updating'}
            readOnly={authState !== 'authenticated'}
            icon={authState === 'authenticated' ? 'unlock' : 'lock'}
            iconPosition="left"
            placeholder={t('newPassword')}
            fluid
            type="password"
            name="password"
            value={formik.values.password}
            onChange={formik.handleChange}
            onClick={() => authState !== 'authenticated' && setOpenModal(true)}
          />
          <span className="label">{t('confirmNewPassword')}</span>
          <Form.Input
            error={formik.touched.confirmPassword ? _.get(formik.errors, 'confirmPassword') : null}
            disabled={updateState === 'updating'}
            readOnly={authState !== 'authenticated'}
            icon={authState === 'authenticated' ? 'unlock' : 'lock'}
            iconPosition="left"
            placeholder={t('confirmNewPassword')}
            fluid
            type="password"
            name="confirmPassword"
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onClick={() => authState !== 'authenticated' && setOpenModal(true)}
          />
          <div className="btnBox">
            <Button
              fluid
              basic
              type="submit"
              className="vButton colorPrimary"
              size="large"
              onClick={formik.handleSubmit}
              loading={updateState === 'updating'}
              content={t('acceptChanges')}
            />
            <Button
              disabled={updateState === 'updating'}
              fluid
              className="vButton outlinePrimary"
              size="large"
              onClick={formik.resetForm}
              content={t('cancel')}
            />
          </div>
        </Segment>
      </Form>

      <Modal size="mini" open={openModal} onClose={formikAuth.handleReset}>
        <Modal.Content>
          <Form
            size="large"
            className="VForm"
            onKeyDown={e => e.key === 'Enter' && formikAuth.handleSubmit()}
          >
            <Header as="h2" textAlign="center" content={t('confirmCurrentPassword')} />
            <Segment basic>
              <Form.Input
                error={formikAuth.touched.password ? formikAuth.errors.password : null}
                disabled={authState === 'authenticating'}
                required
                fluid
                icon="lock"
                placeholder={t('password')}
                type="password"
                name="password"
                value={formikAuth.values.password}
                onChange={formikAuth.handleChange}
              />
            </Segment>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button content={t('cancel')} onClick={formikAuth.handleReset} />
          <Button
            size="large"
            style={{ marginTop: 45, marginBottom: 15 }}
            loading={authState === 'authenticating'}
            onClick={formikAuth.handleSubmit}
            content={t('authenticatePassword')}
          />
        </Modal.Actions>
      </Modal>
    </>
  );
};

export default AccountForm;
